/**
 * Credit gate — one place that answers "can the user still do anything?"
 * =====================================================================
 *
 * Without this, an empty balance is invisible until something fails: the user
 * clicks Generate, nothing happens (or a raw backend error flashes by), and
 * automations stop server-side with no trace in the UI. This provider makes the
 * state explicit and gives every button a single guard to call.
 *
 * What it offers:
 *   balance          current credits (null while loading)
 *   isExhausted      balance no longer covers the CHEAPEST action — literally
 *                    nothing can be started
 *   canAfford(cost)  pure check, no side effects
 *   requireCredits(cost)
 *                    the guard for click handlers: returns true when the action
 *                    can run, otherwise opens the modal and returns false
 *   openCreditsModal(cost)
 *                    open it manually (e.g. after a backend 402)
 *
 * The automatic popup shows ONCE per page load, deliberately not persisted:
 * a refresh is how users retry, so they should be reminded again. It re-arms
 * when a top-up arrives and the balance runs out again in the same session.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useCreditBalance from './useCreditBalance';
import { CREDIT_COST_DATA, quantizeCredits } from '../config/creditPlans';
import CreditsExhaustedModal, { CreditsExhaustedBanner } from '../WorldDash/CreditsExhaustedModal';

/**
 * Cheapest chargeable action in the price list (Contact Clean at the time of
 * writing). Derived instead of hardcoded so it follows the price list.
 */
export const MIN_ACTION_COST = Math.min(
  ...Object.values(CREDIT_COST_DATA.actions || {}).map((a) => {
    const eur = (Number(a.eurCost) || 0) * (Number(a.vendorSurcharge) || 1);
    // Quantized exactly like the charged prices — otherwise the threshold sits
    // a fraction below the cheapest real charge, and a balance of 0.14 would
    // count as "not empty" while nothing can actually be paid for.
    return quantizeCredits(eur / CREDIT_COST_DATA.economics.anchorEurPerCredit);
  })
);

const CreditGateContext = createContext(null);

/**
 * Worlds that already had their popup this page load. Module scope on purpose:
 * component state would reset when the layout unmounts (a detour to the team
 * dashboard and back), and the user would get the same popup again. A refresh
 * clears the module, which is exactly the "once per session, again after a
 * refresh" behaviour we want. Not persisted anywhere else.
 */
const announcedWorlds = new Set();

export function CreditGateProvider({ userId, children }) {
  const { balance, loading, sharedPool, readFailed } = useCreditBalance(userId);
  const [modal, setModal] = useState(null); // null | { requiredCost }

  // `readFailed` matters: a denied read (missing RTDB grant for a team member
  // or sub-world) reports a 0 placeholder. Treating that as "empty" would tell
  // a customer with 30k credits that they are out of them, and block every
  // button. Unknown therefore means "carry on" — the server-side deduction is
  // the authority and rejects for real when the pool is actually empty.
  const isExhausted = !loading && !readFailed && balance !== null && balance < MIN_ACTION_COST;

  useEffect(() => {
    if (!userId) return;
    if (isExhausted && !announcedWorlds.has(userId)) {
      // Tracked per world, so switching to another (also empty) team world
      // announces itself instead of staying silent.
      announcedWorlds.add(userId);
      setModal({ requiredCost: null });
    }
    // Credits came back — arm the popup again in case they run out later.
    if (!isExhausted) announcedWorlds.delete(userId);
  }, [isExhausted, userId]);

  const canAfford = useCallback(
    (cost = MIN_ACTION_COST) => {
      // Unknown balance (still loading, or the read was denied) must not block:
      // the server-side deduction is the real authority and will reject if the
      // pool is truly empty.
      if (loading || readFailed || balance === null) return true;
      return balance >= (Number(cost) || 0);
    },
    [balance, loading, readFailed]
  );

  const openCreditsModal = useCallback((requiredCost = null) => {
    setModal({ requiredCost });
  }, []);

  const requireCredits = useCallback(
    (cost = MIN_ACTION_COST) => {
      if (canAfford(cost)) return true;
      setModal({ requiredCost: Number(cost) || null });
      return false;
    },
    [canAfford]
  );

  const value = useMemo(
    () => ({
      balance,
      loading,
      sharedPool,
      isExhausted,
      canAfford,
      requireCredits,
      openCreditsModal,
    }),
    [balance, loading, sharedPool, isExhausted, canAfford, requireCredits, openCreditsModal]
  );

  return (
    <CreditGateContext.Provider value={value}>
      {children}
      {isExhausted && (
        <CreditsExhaustedBanner balance={balance || 0} onOpen={() => openCreditsModal()} />
      )}
      {modal && (
        <CreditsExhaustedModal
          balance={balance || 0}
          requiredCost={modal.requiredCost}
          sharedPool={sharedPool}
          onClose={() => setModal(null)}
        />
      )}
    </CreditGateContext.Provider>
  );
}

/**
 * Components outside the provider (public pages, team dashboard) still call
 * this, so it degrades to "everything allowed" rather than throwing — the
 * server-side deduction remains the real gate.
 */
const FALLBACK = {
  balance: null,
  loading: true,
  sharedPool: false,
  isExhausted: false,
  canAfford: () => true,
  requireCredits: () => true,
  openCreditsModal: () => {},
};

export default function useCreditGate() {
  return useContext(CreditGateContext) || FALLBACK;
}
